import { Clear } from '@mui/icons-material';
import {
    Avatar,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import ActionButton from 'common/components/ActionButton';
import CustomSelectValidator from 'common/components/CustomSelectValidators';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import { messageNotAuthorize } from 'common/utils/CONST';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { fetchUsersAction } from 'redux/slices/userSlice';

import useColorsStyles from 'common/colors';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import SelectChipsInputUsers from 'common/components/labels/SelectChipsInputUsers';
import UserRole from 'common/enums/roles';
import useFlexBoxStyles from 'common/flexBox';
import useFontStyles from 'common/fontStyles';
import EventFormActorForm from './EventFormActorForm';

const EventFormActors = (props) => {
    const { type, event, handleFieldChange, actors, setActors, step, setStep, cancel, guestLimit } = props;
    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: font } = useFontStyles();
    const { classes: color } = useColorsStyles();

    const dispatch = useDispatch();

    const user = useSelector(getUser);
    const isAuthorize = useAuthLicenseAddOns();
    const [users, setUsers] = useState([]);
    const [useChatManager, setUseChatManager] = useState(event?.chatManagerUsersIds?.length > 0);

    useEffect(() => {
        if (!useChatManager) {
            handleFieldChange({
                target: {
                    name: 'chatManagerUsersIds',
                    value: [],
                },
            });
        }
    }, [useChatManager, handleFieldChange]);

    useEffect(() => {
        if (user.role === 'creator') {
            let userTemp = {
                ...user,
                id: user.userId,
            };
            setUsers([userTemp]);
        } else {
            dispatch(
                fetchUsersAction({
                    queryParams: { limit: 1000 },
                    onSuccess: (data) => {
                        setUsers(data.items);
                    },
                }),
            );
        }
    }, [dispatch, user]);

    const adminActor = useCallback(() => {
        const adminUser =
            user.userId === event.adminUserId
                ? { ...user, id: user.userId }
                : users.find((u) => u.id === event.adminUserId);
        return adminUser
            ? {
                  userId: event.adminUserId,
                  isAdmin: true,
                  email: adminUser?.email,
                  avatar: adminUser?.avatar,
                  role: event.isAdminHost ? 'host' : 'admin',
              }
            : null;
    }, [event.adminUserId, event.isAdminHost, user, users]);

    const chatManagerActors = useCallback(() => {
        let chatManagerUsers = [];
        event?.chatManagerUsersIds?.forEach((chm) => {
            let chatManagerUser = {};
            if (chm === user.userId) {
                chatManagerUser = { ...user, id: user.userId };
            }
            chatManagerUser = users.find((u) => u.id === chm);
            if (chatManagerUser) {
                chatManagerUsers = [
                    ...chatManagerUsers,
                    {
                        userId: chatManagerUser.id,
                        isAdmin: false,
                        email: chatManagerUser?.email,
                        avatar: chatManagerUser?.avatar,
                        role: 'chat_manager',
                    },
                ];
            }
        });
        return chatManagerUsers;
    }, [event?.chatManagerUsersIds, user, users]);

    useEffect(() => {
        let index = event?.chatManagerUsersIds?.findIndex((chm) => chm === event.adminUserId);
        if (index !== -1) {
            const value = [...event.chatManagerUsersIds.slice(0, index), ...event.chatManagerUsersIds.slice(index + 1)];
            handleFieldChange({
                target: {
                    name: 'chatManagerUsersIds',
                    value: value,
                },
            });
        }
    }, [actors, event.adminUserId, event?.chatManagerUsersIds, handleFieldChange]);

    const handleChatManagersChange = (e) => {
        handleFieldChange({
            target: {
                name: 'chatManagerUsersIds',
                value: e,
            },
        });
    };

    useEffect(() => {
        setActors((actors) => {
            const admin = adminActor();
            let _actors = [...actors];
            if (event.isAdminHost) {
                const hostIndex = _actors.findIndex((actor) => actor.role === 'host');
                if (hostIndex !== -1) {
                    _actors = [..._actors.slice(0, hostIndex), ..._actors.slice(hostIndex + 1, _actors.length)];
                }
                const adminIndex = _actors.findIndex((actor) => actor.isAdmin);
                if (adminIndex !== -1) {
                    _actors = [..._actors.slice(0, adminIndex), ..._actors.slice(adminIndex + 1, _actors.length)];
                }
                _actors = [...(admin ? [admin] : []), ..._actors];
            } else {
                const adminIndex = _actors.findIndex((actor) => actor.isAdmin);
                if (adminIndex !== -1) {
                    _actors = [
                        ...(admin ? [admin] : []),
                        ..._actors.slice(0, adminIndex),
                        ..._actors.slice(adminIndex + 1, actors.length),
                    ];
                } else {
                    _actors = [...(admin ? [admin] : []), ..._actors];
                }
            }

            const chatManagers = chatManagerActors();

            _actors = _actors.filter((actor) => actor.role !== 'chat_manager');

            chatManagers.forEach((chatManager) => {
                _actors = [...(chatManager ? [chatManager] : []), ..._actors];
            });

            return _actors;
        });
    }, [event.adminUserId, event.isAdminHost, users, adminActor, chatManagerActors, setActors]);

    const validateForm = () => {
        const actorsValid =
            actors.length > 0 && actors.filter((actor) => actor.role === 'host').length === 1 && event.adminUserId;
        const chatManagers = event?.chatManagerUsersIds?.length > 0;
        const chatManager = useChatManager ? chatManagers : true;
        return actorsValid && chatManager;
    };

    const handleAddActorSubmit = (fields, reset) => {
        if (!actors.find((actor) => actor.email === fields.email)) {
            if (fields.role === 'host') {
                setActors([
                    ...actors,
                    {
                        isAdmin: false,
                        name: fields.name,
                        email: fields.email,
                        role: fields.role,
                    },
                ]);
            } else {
                setActors([
                    ...actors,
                    {
                        email: fields.email,
                        role: fields.role,
                    },
                ]);
            }
            reset();
        }
    };

    const deleteActor = (index) => {
        if (index >= 0 && index < actors.length) {
            setActors([...actors.slice(0, index), ...actors.slice(index + 1, actors.length)]);
        }
    };

    ValidatorForm.addValidationRule('emailAdmin', (value) => {
        const admin = adminActor();
        return admin?.email !== value;
    });

    const chatManagerCheck = (
        <FormGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        className={cx(flexBox.padding0, flexBox.margin01)}
                        checked={useChatManager}
                        onChange={(e) => setUseChatManager(e.target.checked)}
                        name="useChatManager"
                        color="primary"
                        disabled={!isAuthorize(['professional_pack'])}
                    />
                }
                label={<FormLabel>{t('event.eventFormActors.useChatManager')}</FormLabel>}
            />
        </FormGroup>
    );

    const canTransferTheICallOwnership = () => {
        return type === 'one_to_one' && user.role !== UserRole.CREATOR;
    };

    const submit = () => {
        console.debug('submit');
    };

    return (
        <ValidatorForm
            onSubmit={submit}
            onError={(e) => {}}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
        >
            <Stack
                direction="column"
                spacing={3}
                className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
            >
                {(type === 'one_to_many' || canTransferTheICallOwnership()) && (
                    <FormGroup>
                        <FormLabel>
                            {t(
                                type === 'one_to_many'
                                    ? 'event.eventFormActors.eventAdmin'
                                    : 'event.eventFormActors.changeAdmin',
                            )}
                        </FormLabel>

                        <CustomSelectValidator
                            name="adminUserId"
                            select
                            size="small"
                            fullWidth={true}
                            value={users?.length ? event.adminUserId : ''}
                            onChange={handleFieldChange}
                            validators={['required']}
                            errorMessages={[t('app.common.required')]}
                            variant="outlined"
                        >
                            {users.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.firstName + ' ' + user.lastName}
                                </MenuItem>
                            ))}
                        </CustomSelectValidator>
                    </FormGroup>
                )}
                {type !== 'one_to_one' && (
                    <>
                        {event.hostProvider === 'web' && (
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={event.isAdminHost}
                                            className={cx(flexBox.padding0, flexBox.margin01)}
                                            onChange={handleFieldChange}
                                            name="isAdminHost"
                                            color="primary"
                                            disabled={
                                                !event.adminUserId ||
                                                users.length === 0 ||
                                                actors.find(
                                                    (actor) =>
                                                        !actor.isAdmin &&
                                                        actor.email ===
                                                            users.find((user) => user.id === event.adminUserId)?.email,
                                                ) ||
                                                actors.find((actor) => !actor.isAdmin && actor.role === 'host')
                                            }
                                        />
                                    }
                                    label={<FormLabel>{t('event.eventFormActors.adminIsHost')}</FormLabel>}
                                />
                            </FormGroup>
                        )}

                        {!isAuthorize(['professional_pack']) ? (
                            <CustomTooltip arrow title={t(messageNotAuthorize)} placement={'left'}>
                                {chatManagerCheck}
                            </CustomTooltip>
                        ) : (
                            chatManagerCheck
                        )}

                        {useChatManager && (
                            <FormGroup>
                                <FormLabel>{t('event.eventFormActors.eventChatManager')}</FormLabel>

                                <FormControl>
                                    <SelectChipsInputUsers
                                        selectedOptions={event?.chatManagerUsersIds}
                                        onChange={handleChatManagersChange}
                                        placeholder={t('event.formActors.selectChatManagers')}
                                        optionsRender={users?.filter((user) => event?.adminUserId !== user.id)}
                                    />
                                </FormControl>
                            </FormGroup>
                        )}
                    </>
                )}

                <EventFormActorForm
                    event={event}
                    actors={actors}
                    onSubmit={handleAddActorSubmit}
                    guestLimit={guestLimit}
                />

                {actors.map((actor, index) => {
                    return !actor.isAdmin &&
                        actor.role !== 'chat_manager' &&
                        (actor.role !== 'guest' || !event.record) ? (
                        <React.Fragment key={actor.email}>
                            <Stack direction="row" alignItems="center" spacing={2} className={flexBox.w100}>
                                <Avatar
                                    className={color.textWhite}
                                    key={actor.email}
                                    src={actor.avatar || '/broken-image.jpg'}
                                />

                                <Stack direction="column" className={flexBox.w60}>
                                    {actor.name && <Typography variant="subtitle1">{actor.name}</Typography>}
                                    <CustomTooltip arrow title={actor.email} placement={'bottom'}>
                                        <Typography
                                            variant="subtitle1"
                                            className={font.overflowEllipsis}
                                            fontWeight={500}
                                        >
                                            {actor.email}
                                        </Typography>
                                    </CustomTooltip>
                                </Stack>

                                <Typography
                                    variant="subtitle1"
                                    className={cx(flexBox.w30, font.overflowEllipsis, font.textCapitalize)}
                                    fontWeight={500}
                                >
                                    {actor.role}
                                </Typography>
                                <CustomIconButton size="small" hideBorder={true} onClick={() => deleteActor(index)}>
                                    <Clear />
                                </CustomIconButton>
                            </Stack>
                            {index + 1 < actors.length && <Divider />}
                        </React.Fragment>
                    ) : null;
                })}
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    disabled={!validateForm()}
                    onClick={() => setStep(step + 1)}
                >
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default EventFormActors;
